import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, IconButton, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import Replay10Icon from '@mui/icons-material/Replay10';
import Forward10Icon from '@mui/icons-material/Forward10';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import StraightenIcon from '@mui/icons-material/Straighten';
import VerifiedIcon from '@mui/icons-material/Verified';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import type { TimelineMarkerIcon } from '@/components/review/consensus';
import type { JudgementOption, SessionEvent } from '@/types';
import { formatDecisionSummary, formatVideoTime, getJudgementDotColor, getJudgementLabel } from '@/types';
import { useMobileLayout } from '@/hooks/useMobileLayout';
import { darkScrollbarSx } from '@/utils/themeSurfaces';

export interface TimelinePlayerControls {
  currentTime: number;
  playbackRate: number;
  onTogglePlay: () => void;
  onPlaybackRateChange: (rate: number) => void;
  isPlaying?: boolean;
}

interface Props {
  events: SessionEvent[];
  videoDuration: number;
  currentTime?: number;
  selectedEventId?: string | null;
  onSelect?: (event: SessionEvent) => void;
  onSeek?: (seconds: number) => void;
  playerControls?: TimelinePlayerControls;
  dark?: boolean;
  /** Force compact mobile controls (e.g. fullscreen pro mode in landscape). */
  compact?: boolean;
  /** Hide judgement dot colors (teaching mode). */
  educationMode?: boolean;
  /** Flush inside ReviewVideoPlayerShell — no outer card border. */
  embedded?: boolean;
  /** Pro mode: +/- zoom and horizontal scroll on the scrub track. */
  zoomable?: boolean;
  timelineZoom?: number;
  onTimelineZoomChange?: (zoom: number) => void;
  /** Verified / groups / person badges on scrub markers (integrated timeline & own marks). */
  resolveMarkerIcon?: (event: SessionEvent) => TimelineMarkerIcon | null;
}

const TIMELINE_ZOOM_MIN = 1;
export const TIMELINE_ZOOM_MAX = 5;
const TIMELINE_ZOOM_STEP = 1;

function clampTimelineZoom(value: number): number {
  const stepped = Math.round(value);
  return Math.min(TIMELINE_ZOOM_MAX, Math.max(TIMELINE_ZOOM_MIN, stepped));
}

export { clampTimelineZoom };

const TIMELINE_ZOOM_TIERS =
  Math.round((TIMELINE_ZOOM_MAX - TIMELINE_ZOOM_MIN) / TIMELINE_ZOOM_STEP) + 1;
const TIMELINE_ZOOM_BAR_HEIGHTS = [5, 7, 9, 11, 13];

function timelineZoomTier(zoom: number): number {
  const z = clampTimelineZoom(zoom);
  return Math.round((z - TIMELINE_ZOOM_MIN) / TIMELINE_ZOOM_STEP) + 1;
}

function formatTimelineZoomLabel(zoom: number): string {
  return `${clampTimelineZoom(zoom)}x`;
}

function TimelineZoomLevelBars({
  tier,
  activeColor,
  inactiveColor,
}: {
  tier: number;
  activeColor: string;
  inactiveColor: string;
}) {
  return (
    <Stack direction="row" alignItems="flex-end" spacing={0.35} sx={{ height: 14, px: 0.25 }}>
      {Array.from({ length: TIMELINE_ZOOM_TIERS }, (_, i) => (
        <Box
          key={i}
          sx={{
            width: 2.5,
            height: TIMELINE_ZOOM_BAR_HEIGHTS[i],
            borderRadius: 0.5,
            bgcolor: i < tier ? activeColor : inactiveColor,
            transition: 'background-color 0.15s ease',
          }}
        />
      ))}
    </Stack>
  );
}

/** Seconds before marker timestamp when jumping to an event. */
export const EVENT_PREROLL = 0.8;

function resolveJudgementOption(value?: string | null): JudgementOption | null {
  if (value === 'correct') return 'correct';
  if (value === 'wrong' || value === 'incorrect') return 'wrong';
  if (value === 'uncertain') return 'uncertain';
  return null;
}

const SPEEDS = [0.25, 0.5, 1, 1.5, 2] as const;

/** Tolerance around the seek target (event time − preroll). */
const AT_EVENT_TOLERANCE = 0.55;

function eventSeekTarget(event: SessionEvent): number {
  return Math.max(0, (event.video_second ?? 0) - EVENT_PREROLL);
}

function positionPct(second: number, duration: number): number {
  if (duration <= 0) return 0;
  return Math.min(100, Math.max(0, (second / duration) * 100));
}

export function resolveEventNavigation(markedEvents: SessionEvent[], playbackTime: number) {
  if (markedEvents.length === 0) {
    return { prevEvent: null as SessionEvent | null, nextEvent: null as SessionEvent | null, currentIndex: -1 };
  }

  let currentIndex = -1;
  let closestDist = AT_EVENT_TOLERANCE;
  for (let i = 0; i < markedEvents.length; i++) {
    const dist = Math.abs(eventSeekTarget(markedEvents[i]) - playbackTime);
    if (dist <= closestDist) {
      closestDist = dist;
      currentIndex = i;
    }
  }

  if (currentIndex >= 0) {
    return {
      prevEvent: currentIndex > 0 ? markedEvents[currentIndex - 1] : null,
      nextEvent: currentIndex < markedEvents.length - 1 ? markedEvents[currentIndex + 1] : null,
      currentIndex,
    };
  }

  const nextIndex = markedEvents.findIndex((e) => eventSeekTarget(e) > playbackTime + 0.15);
  if (nextIndex === -1) {
    return {
      prevEvent: markedEvents[markedEvents.length - 1],
      nextEvent: null,
      currentIndex: -1,
    };
  }
  if (nextIndex === 0) {
    return { prevEvent: null, nextEvent: markedEvents[0], currentIndex: -1 };
  }
  return {
    prevEvent: markedEvents[nextIndex - 1],
    nextEvent: markedEvents[nextIndex],
    currentIndex: -1,
  };
}

export default function MatchEventHorizontalTimeline({
  events,
  videoDuration,
  currentTime = 0,
  selectedEventId,
  onSelect,
  onSeek,
  playerControls,
  dark = false,
  compact,
  educationMode = false,
  embedded = false,
  zoomable = false,
  timelineZoom = 1,
  onTimelineZoomChange,
  resolveMarkerIcon,
}: Props) {
  const theme = useTheme();
  const isDarkUi = dark || theme.palette.mode === 'dark';
  const { isMobile: isMobileViewport } = useMobileLayout();
  const isMobile = compact ?? isMobileViewport;
  const duration = Math.max(videoDuration, 1);
  const scrubTrackRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const userScrolledRef = useRef(false);
  const scrollIdleTimerRef = useRef<number | null>(null);
  const [scrubbing, setScrubbing] = useState(false);
  const [trackHovered, setTrackHovered] = useState(false);
  const zoom = zoomable ? clampTimelineZoom(timelineZoom) : 1;

  const markedEvents = useMemo(
    () =>
      events
        .filter((e) => e.video_second != null)
        .sort((a, b) => (a.video_second ?? 0) - (b.video_second ?? 0)),
    [events]
  );

  const playbackTime = playerControls?.currentTime ?? currentTime;
  const playheadPct = positionPct(playbackTime, duration);
  const playedPct = playheadPct;

  const { prevEvent, nextEvent, currentIndex } = resolveEventNavigation(markedEvents, playbackTime);

  const borderColor = isDarkUi ? 'rgba(255,255,255,0.1)' : alpha(theme.palette.divider, 0.9);
  const textSecondary = isDarkUi ? 'rgba(255,255,255,0.6)' : 'text.secondary';
  const trackBg = isDarkUi ? 'rgba(255,255,255,0.12)' : alpha(theme.palette.action.selected, 0.5);
  const trackPlayed = dark ? theme.palette.secondary.main : theme.palette.secondary.main;
  const trackHeight = trackHovered || scrubbing ? (isMobile ? 6 : 8) : isMobile ? 4 : 6;

  const prevTip = prevEvent
    ? `上一個標記 ${formatVideoTime(prevEvent.video_second ?? 0)}`
    : '沒有上一個標記';
  const nextTip = nextEvent
    ? `下一個標記 ${formatVideoTime(nextEvent.video_second ?? 0)}`
    : '沒有下一個標記';

  const jumpToEvent = (event: SessionEvent) => {
    const target = eventSeekTarget(event);
    onSelect?.(event);
    onSeek?.(target);
    scrollToTime(target);
  };

  const scrollToTime = useCallback(
    (time: number) => {
      if (!zoomable || zoom <= 1 || !scrollRef.current || videoDuration <= 0) return;
      const innerWidth = scrollRef.current.scrollWidth;
      const viewWidth = scrollRef.current.clientWidth;
      if (viewWidth <= 0) return;
      const playheadX = (time / duration) * innerWidth;
      scrollRef.current.scrollLeft = Math.max(0, Math.min(innerWidth - viewWidth, playheadX - viewWidth / 2));
    },
    [zoomable, zoom, duration, videoDuration]
  );

  const seekBy = (delta: number) => {
    if (!playerControls) return;
    const max = videoDuration > 0 ? videoDuration : Infinity;
    const next = Math.max(0, Math.min(max, playerControls.currentTime + delta));
    onSeek?.(next);
    scrollToTime(next);
  };

  const seekFromPointer = useCallback(
    (clientX: number) => {
      if (!onSeek || !scrubTrackRef.current) return;
      const track = scrubTrackRef.current;
      const rect = track.getBoundingClientRect();
      const trackWidth = track.offsetWidth;
      if (trackWidth <= 0) return;
      // getBoundingClientRect already reflects scroll — do not add scrollLeft
      const xOnTrack = clientX - rect.left;
      const ratio = Math.min(1, Math.max(0, xOnTrack / trackWidth));
      onSeek(ratio * duration);
    },
    [duration, onSeek]
  );

  /** At 1× zoom the track fits the viewport — clear any scroll offset from denser zoom. */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || zoom > 1) return;
    el.scrollLeft = 0;
    userScrolledRef.current = false;
  }, [zoom]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !zoomable || zoom <= 1) return;
    const onScroll = () => {
      userScrolledRef.current = true;
      if (scrollIdleTimerRef.current != null) window.clearTimeout(scrollIdleTimerRef.current);
      scrollIdleTimerRef.current = window.setTimeout(() => {
        userScrolledRef.current = false;
        scrollIdleTimerRef.current = null;
      }, 2500);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (scrollIdleTimerRef.current != null) window.clearTimeout(scrollIdleTimerRef.current);
    };
  }, [zoomable, zoom]);

  useEffect(() => {
    if (!scrubbing) return;
    const onMove = (e: PointerEvent) => seekFromPointer(e.clientX);
    const onUp = () => setScrubbing(false);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp, { once: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [scrubbing, seekFromPointer]);

  useEffect(() => {
    if (!zoomable || zoom <= 1 || !scrollRef.current || videoDuration <= 0) return;
    if (scrubbing) return;
    if (playerControls && !playerControls.isPlaying) return;
    if (userScrolledRef.current) return;
    scrollToTime(playbackTime);
  }, [zoomable, zoom, playbackTime, videoDuration, playerControls?.isPlaying, scrubbing, scrollToTime]);

  const adjustTimelineZoom = (delta: number) => {
    if (!onTimelineZoomChange) return;
    onTimelineZoomChange(clampTimelineZoom(zoom + delta));
  };

  const timeLabel = `${formatVideoTime(playbackTime)} / ${
    videoDuration > 0 ? formatVideoTime(videoDuration) : '—'
  }`;

  const transportBtnSx = {
    color: isDarkUi ? 'rgba(255,255,255,0.92)' : theme.palette.text.primary,
    bgcolor: isDarkUi ? 'rgba(255,255,255,0.08)' : theme.palette.common.white,
    border: '1px solid',
    borderColor: isDarkUi ? 'rgba(255,255,255,0.12)' : theme.palette.divider,
    boxShadow: isDarkUi ? 'none' : `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
    '&:hover': {
      bgcolor: isDarkUi ? 'rgba(255,255,255,0.14)' : alpha(theme.palette.secondary.main, 0.1),
      borderColor: isDarkUi ? 'rgba(255,255,255,0.22)' : alpha(theme.palette.secondary.main, 0.45),
    },
    '&.Mui-disabled': {
      color: isDarkUi ? 'rgba(255,255,255,0.28)' : theme.palette.action.disabled,
      bgcolor: isDarkUi ? 'rgba(255,255,255,0.04)' : alpha(theme.palette.action.disabledBackground, 0.5),
      borderColor: isDarkUi ? 'rgba(255,255,255,0.06)' : theme.palette.divider,
    },
  };

  const playBtnSx = {
    ...transportBtnSx,
    bgcolor: theme.palette.secondary.main,
    color: theme.palette.getContrastText(theme.palette.secondary.main),
    borderColor: alpha(theme.palette.secondary.dark, isDarkUi ? 0.5 : 0.35),
    boxShadow: isDarkUi ? 'none' : `0 1px 3px ${alpha(theme.palette.secondary.dark, 0.25)}`,
    '&:hover': {
      bgcolor: theme.palette.secondary.dark,
      color: theme.palette.getContrastText(theme.palette.secondary.dark),
      borderColor: theme.palette.secondary.dark,
    },
  };

  const speedChips = playerControls
    ? SPEEDS.map((s) => (
        <Chip
          key={s}
          label={`${s}x`}
          size="small"
          variant={playerControls.playbackRate === s ? 'filled' : 'outlined'}
          color={playerControls.playbackRate === s ? 'secondary' : 'default'}
          onClick={() => playerControls.onPlaybackRateChange(s)}
          sx={{
            cursor: 'pointer',
            height: 24,
            fontSize: '0.72rem',
            fontWeight: playerControls.playbackRate === s ? 700 : 500,
            ...(isDarkUi && playerControls.playbackRate !== s
              ? { borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.75)' }
              : {}),
          }}
        />
      ))
    : null;

  const trackCenterY = isMobile ? 28 : 34;

  const transportControls = (
    <>
      <Tooltip title={prevTip}>
        <span>
          <IconButton
            size="small"
            sx={transportBtnSx}
            onClick={() => prevEvent && jumpToEvent(prevEvent)}
            disabled={!prevEvent}
            aria-label={prevTip}
          >
            <SkipPreviousIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <IconButton size="small" sx={transportBtnSx} onClick={() => seekBy(-10)} aria-label="後退 10 秒">
        <Replay10Icon fontSize="small" />
      </IconButton>
      <IconButton size="small" sx={playBtnSx} onClick={playerControls!.onTogglePlay} aria-label="播放/暫停">
        {playerControls!.isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
      </IconButton>
      <IconButton size="small" sx={transportBtnSx} onClick={() => seekBy(10)} aria-label="前進 10 秒">
        <Forward10Icon fontSize="small" />
      </IconButton>
      <Tooltip title={nextTip}>
        <span>
          <IconButton
            size="small"
            sx={transportBtnSx}
            onClick={() => nextEvent && jumpToEvent(nextEvent)}
            disabled={!nextEvent}
            aria-label={nextTip}
          >
            <SkipNextIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );

  const timeAndMarkers = (
    <>
      <Typography
        variant="caption"
        sx={{
          color: textSecondary,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '0.02em',
        }}
      >
        {timeLabel}
      </Typography>
      {markedEvents.length > 0 && (
        <Chip
          size="small"
          label={`${markedEvents.length} 標記`}
          sx={{
            height: 22,
            fontSize: '0.68rem',
            ...(isDarkUi
              ? { bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)' }
              : { bgcolor: alpha(theme.palette.secondary.main, 0.08) }),
          }}
        />
      )}
    </>
  );

  const timelineZoomControls =
    zoomable && onTimelineZoomChange ? (
      <Stack direction="row" alignItems="center" spacing={0.25}>
        <Tooltip title="縮小時間軸">
          <span>
            <IconButton
              size="small"
              sx={transportBtnSx}
              onClick={() => adjustTimelineZoom(-TIMELINE_ZOOM_STEP)}
              disabled={zoom <= TIMELINE_ZOOM_MIN}
              aria-label="縮小時間軸"
            >
              <RemoveIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={`時間軸密度 ${formatTimelineZoomLabel(zoom)}`}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            aria-label={`時間軸密度 ${formatTimelineZoomLabel(zoom)}`}
            sx={{
              px: 0.75,
              py: 0.375,
              borderRadius: 1,
              border: '1px solid',
              borderColor: zoom > 1
                ? alpha(theme.palette.secondary.main, 0.45)
                : isDarkUi
                  ? 'rgba(255,255,255,0.1)'
                  : theme.palette.divider,
              bgcolor: zoom > 1
                ? alpha(theme.palette.secondary.main, isDarkUi ? 0.18 : 0.1)
                : isDarkUi
                  ? 'rgba(255,255,255,0.06)'
                  : alpha(theme.palette.action.selected, 0.4),
              userSelect: 'none',
            }}
          >
            <StraightenIcon
              sx={{
                fontSize: 15,
                color: zoom > 1 ? theme.palette.secondary.main : textSecondary,
                transform: 'rotate(90deg)',
              }}
            />
            <TimelineZoomLevelBars
              tier={timelineZoomTier(zoom)}
              activeColor={zoom > 1 ? theme.palette.secondary.main : alpha(theme.palette.text.primary, 0.55)}
              inactiveColor={isDarkUi ? 'rgba(255,255,255,0.18)' : alpha(theme.palette.text.primary, 0.18)}
            />
          </Stack>
        </Tooltip>
        <Tooltip title="放大時間軸">
          <span>
            <IconButton
              size="small"
              sx={transportBtnSx}
              onClick={() => adjustTimelineZoom(TIMELINE_ZOOM_STEP)}
              disabled={zoom >= TIMELINE_ZOOM_MAX}
              aria-label="放大時間軸"
            >
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    ) : null;

  return (
    <Box
      sx={{
        borderRadius: embedded ? 0 : 2.5,
        border: embedded ? 'none' : '1px solid',
        borderColor,
        bgcolor: embedded ? 'transparent' : isDarkUi ? 'rgba(255,255,255,0.03)' : 'background.paper',
        boxShadow: embedded || isDarkUi ? 'none' : `0 1px 8px ${alpha(theme.palette.common.black, 0.06)}`,
        overflow: 'hidden',
      }}
    >
      {playerControls && (
        <Box
          sx={{
            px: isMobile ? 1.25 : 1.75,
            py: isMobile ? 0.875 : 1.125,
            borderBottom: '1px solid',
            borderColor,
            bgcolor: isDarkUi ? 'rgba(0,0,0,0.2)' : theme.palette.grey[50],
          }}
        >
          {isMobile ? (
            <Stack spacing={1} alignItems="center" sx={{ width: '100%' }}>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} useFlexGap>
                {transportControls}
              </Stack>
              {speedChips && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={0.5}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ width: '100%' }}
                >
                  {speedChips}
                </Stack>
              )}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ width: '100%' }}
              >
                {timeAndMarkers}
                {timelineZoomControls}
              </Stack>
            </Stack>
          ) : (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
            >
              <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                {transportControls}
                {speedChips && (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 0.75 }}>
                    {speedChips}
                  </Stack>
                )}
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
                {timeAndMarkers}
                {timelineZoomControls}
              </Stack>
            </Stack>
          )}
        </Box>
      )}

      <Box sx={{ px: isMobile ? 1.5 : 2, pb: isMobile ? 1.25 : 1.75, pt: isMobile ? 1.25 : 1.5 }}>
        <Box
          ref={scrollRef}
          sx={{
            overflowX: zoom > 1 ? 'auto' : 'hidden',
            overflowY: 'hidden',
            WebkitOverflowScrolling: zoom > 1 ? 'touch' : 'auto',
            mx: zoom > 1 ? -0.5 : 0,
            px: zoom > 1 ? 0.5 : 0,
            ...(zoom <= 1
              ? {
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }
              : isDarkUi
                ? darkScrollbarSx(theme, { forceDark: dark })
                : {}),
          }}
        >
        <Box
          ref={scrubTrackRef}
          onPointerDown={(e) => {
            if ((e.target as HTMLElement).closest('[data-timeline-marker]')) return;
            setScrubbing(true);
            userScrolledRef.current = true;
            if (scrollIdleTimerRef.current != null) window.clearTimeout(scrollIdleTimerRef.current);
            scrollIdleTimerRef.current = window.setTimeout(() => {
              userScrolledRef.current = false;
              scrollIdleTimerRef.current = null;
            }, 2500);
            scrubTrackRef.current?.setPointerCapture(e.pointerId);
            seekFromPointer(e.clientX);
          }}
          onPointerEnter={() => setTrackHovered(true)}
          onPointerLeave={() => setTrackHovered(false)}
          sx={{
            position: 'relative',
            width: zoom > 1 ? `${zoom * 100}%` : '100%',
            maxWidth: zoom > 1 ? undefined : '100%',
            minWidth: zoom > 1 ? '100%' : 0,
            height: isMobile ? 64 : 80,
            touchAction: scrubbing ? 'none' : zoom > 1 ? 'pan-x' : 'none',
            cursor: onSeek ? 'pointer' : 'default',
          }}
        >
          {/* Track background */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: trackCenterY - trackHeight / 2,
              height: trackHeight,
              bgcolor: trackBg,
              borderRadius: trackHeight,
              transition: 'height 0.15s ease',
            }}
          />

          {/* Played progress */}
          {videoDuration > 0 && (
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: trackCenterY - trackHeight / 2,
                width: `${playedPct}%`,
                height: trackHeight,
                bgcolor: alpha(trackPlayed, isDarkUi ? 0.85 : 0.75),
                borderRadius: trackHeight,
                transition: scrubbing ? 'none' : 'width 0.08s linear, height 0.15s ease',
                pointerEvents: 'none',
              }}
            />
          )}

          {/* Event markers */}
          {markedEvents.map((event, idx) => {
            const pct = positionPct(event.video_second ?? 0, duration);
            const selected = selectedEventId === event.id || currentIndex === idx;
            const dotColor = getJudgementDotColor(event.judgement, educationMode);
            const judgementOption = resolveJudgementOption(event.judgement);
            const markerIcon = resolveMarkerIcon?.(event) ?? null;
            const dotSize = selected ? 14 : 11;
            const dotHalf = dotSize / 2;
            const dotTop = trackCenterY - dotHalf;
            const iconGapAboveDot = 8;
            const iconSize = 14;
            const iconTop = dotTop - iconGapAboveDot - iconSize;
            const timeTop = dotTop + dotSize + 3;
            const tip = [
              formatVideoTime(event.video_second ?? 0),
              `場上 ${formatDecisionSummary(event.on_field_decision, event.on_field_punishment)}`,
              judgementOption && !educationMode ? `評價 ${getJudgementLabel(judgementOption)}` : null,
            ]
              .filter(Boolean)
              .join('\n');

            return (
              <Tooltip key={event.id} title={tip} arrow placement="top">
                <Box
                  data-timeline-marker
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    jumpToEvent(event);
                  }}
                  sx={{
                    position: 'absolute',
                    left: `${pct}%`,
                    top: 0,
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: isMobile ? 36 : 44,
                    height: '100%',
                    cursor: 'pointer',
                    zIndex: selected ? 4 : 2,
                    '&:hover .event-marker': { transform: 'translateX(-50%) scale(1.15)' },
                  }}
                >
                  {/* Stem */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: trackCenterY - (isMobile ? 10 : 14),
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 2,
                      height: isMobile ? 10 : 14,
                      bgcolor: selected ? theme.palette.secondary.main : alpha(dotColor, 0.85),
                      borderRadius: 1,
                      opacity: selected ? 1 : 0.7,
                    }}
                  />
                  {/* Role icon above judgement dot */}
                  {!isMobile && markerIcon && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: iconTop,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        lineHeight: 0,
                      }}
                    >
                      {markerIcon === 'verified' && (
                        <VerifiedIcon sx={{ fontSize: iconSize, color: 'secondary.main' }} />
                      )}
                      {markerIcon === 'groups' && (
                        <GroupsIcon
                          sx={{
                            fontSize: iconSize,
                            color: isDarkUi ? 'rgba(255,255,255,0.75)' : 'text.secondary',
                          }}
                        />
                      )}
                      {markerIcon === 'person' && (
                        <PersonIcon
                          sx={{
                            fontSize: iconSize,
                            color: isDarkUi ? 'rgba(255,255,255,0.75)' : 'text.secondary',
                          }}
                        />
                      )}
                    </Box>
                  )}
                  {/* Judgement dot (same as non-fullscreen list uses colour cues) */}
                  <Box
                    className="event-marker"
                    sx={{
                      position: 'absolute',
                      top: dotTop,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: dotSize,
                      height: dotSize,
                      borderRadius: '50%',
                      bgcolor: dotColor,
                      border: '2px solid',
                      borderColor: selected
                        ? theme.palette.secondary.main
                        : isDarkUi
                          ? '#1a1a1a'
                          : '#fff',
                      boxShadow: selected
                        ? `0 0 0 3px ${alpha(theme.palette.secondary.main, 0.45)}`
                        : `0 1px 4px ${alpha(theme.palette.common.black, 0.35)}`,
                      transition: 'transform 0.15s ease, width 0.15s ease, height 0.15s ease',
                    }}
                  />
                  {!isMobile && (
                    <Typography
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        top: timeTop,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '0.62rem',
                        fontWeight: selected ? 700 : 500,
                        color: selected ? 'secondary.main' : textSecondary,
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.1,
                      }}
                    >
                      {formatVideoTime(event.video_second ?? 0)}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            );
          })}

          {/* Playhead knob */}
          {videoDuration > 0 && (
            <Box
              sx={{
                position: 'absolute',
                left: `${playheadPct}%`,
                top: trackCenterY,
                transform: 'translate(-50%, -50%)',
                width: scrubbing || trackHovered ? (isMobile ? 14 : 16) : isMobile ? 10 : 12,
                height: scrubbing || trackHovered ? (isMobile ? 14 : 16) : isMobile ? 10 : 12,
                borderRadius: '50%',
                bgcolor: theme.palette.secondary.main,
                border: '2px solid',
                borderColor: isDarkUi ? '#fff' : '#fff',
                boxShadow: `0 1px 6px ${alpha(theme.palette.common.black, 0.4)}`,
                zIndex: 5,
                pointerEvents: 'none',
                transition: scrubbing ? 'none' : 'left 0.08s linear, width 0.15s ease, height 0.15s ease',
              }}
            />
          )}
        </Box>
        </Box>

        {!isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25, px: 0.25 }}>
            <Typography variant="caption" sx={{ color: textSecondary, fontSize: '0.65rem', fontVariantNumeric: 'tabular-nums' }}>
              0:00
            </Typography>
            <Typography variant="caption" sx={{ color: textSecondary, fontSize: '0.65rem', fontVariantNumeric: 'tabular-nums' }}>
              {videoDuration > 0 ? formatVideoTime(videoDuration) : '—'}
            </Typography>
          </Box>
        )}

        {markedEvents.length === 0 && (
          <Typography variant="caption" sx={{ color: textSecondary, display: 'block', textAlign: 'center', mt: 0.75 }}>
            尚無判決標記 — 在影片中標記判決後會顯示於時間軸
          </Typography>
        )}
      </Box>
    </Box>
  );
}
